// Bootstrap popovers, the counterpart of `bootstrap_tooltip.js`. Nothing in the
// application used them before the network tab, so this is the whole binding.
//
// Two things it adds over `new bootstrap.Popover(element)`:
//
//   * `data-popover-hover-area` names an ancestor selector whose hover shows the
//     popover as well. That is what lets a device's table cell be the hover surface
//     (FR-056) while the trigger stays a real focusable control, so the same content
//     is reachable from the keyboard and by touch (FR-057).
//   * a destructor. Unpoly replaces `#client-network` on every site and network
//     change, and a popover nobody disposed of leaves its tip in the document with
//     no element under it.
//
// Where a hover area is named this drives the popover itself, with the element's
// trigger set to `manual` in the markup. Bootstrap's own hover handling tracks
// state we would then be showing and hiding behind its back, and the two disagree
// as soon as the pointer leaves the row over the trigger.
up.compiler('[data-bs-toggle="popover"]', (element) => {
  const options = {}

  // `data-popover-content` names a `<template>` holding the content as markup.
  // The attribute Bootstrap offers cannot carry it: Haml leaves quotes inside an
  // attribute value alone, so the first `"` in the markup ends the attribute and
  // the rest of the tag lands on the page as text, with nothing to say so.
  const sourceSelector = element.dataset.popoverContent
  const source = sourceSelector ? document.querySelector(sourceSelector) : null
  if (source) {
    options.content = source.innerHTML
  }

  const popover = new bootstrap.Popover(element, options)
  const areaSelector = element.dataset.popoverHoverArea
  const area = areaSelector ? element.closest(areaSelector) : null

  if (!area) {
    return () => popover.dispose()
  }

  // Marked so that every hover area can see the others. Every reveal on a device row
  // answers to its own cell today, but an area named wider than that — the whole row,
  // as the addresses once were — would otherwise show on top of the ones inside it.
  area.dataset.popoverArea = ''

  // The innermost area under the pointer owns the hover, and nothing else shows.
  // Moving between cells therefore hands the reveal over rather than stacking, and
  // an area with no narrower one inside it behaves exactly as before.
  const ownsPointer = (node) => node instanceof Element && node.closest('[data-popover-area]') === area

  let hovered = false
  let focused = false
  let visible = false

  // One owner of the state, so a pointer leaving the row while the button still
  // has focus does not close what the keyboard opened.
  const sync = () => {
    const wanted = hovered || focused
    if (wanted === visible) return

    visible = wanted
    wanted ? popover.show() : popover.hide()
  }

  // `mouseover`/`mouseout` rather than `mouseenter`/`mouseleave`: they fire on every
  // move between descendants, which is what lets the row hand its reveal to a cell
  // and take it back again. `mouseenter` fires once on the way in and would leave
  // whichever won first showing until the pointer left the row altogether.
  const enter = (event) => { hovered = ownsPointer(event.target); sync() }
  const leave = (event) => {
    if (event.relatedTarget instanceof Element && area.contains(event.relatedTarget)) return

    hovered = false
    sync()
  }
  const focus = () => { focused = true; sync() }
  const blur = () => { focused = false; sync() }

  // Tapping the button focuses it, which is the touch route. Escape gives the
  // keyboard a way back out without tabbing on.
  const key = (event) => {
    if (event.key === 'Escape' && visible) {
      element.blur()
    }
  }

  // A reveal that is also a link — a short note, opened on a layer of its own when
  // clicked — gives its popover up as it is followed. The click has just focused it
  // and the pointer is still over its cell, so both would keep the popover showing
  // while the layer loads. A reveal that is only a button keeps its popover on click:
  // on a touch screen, the tap that focuses it is the only way to see it.
  const follow = () => { hovered = false; focused = false; sync() }
  const isLink = element.matches('a[href]')

  area.addEventListener('mouseover', enter)
  area.addEventListener('mouseout', leave)
  element.addEventListener('focus', focus)
  element.addEventListener('blur', blur)
  element.addEventListener('keydown', key)
  if (isLink) element.addEventListener('click', follow)

  return () => {
    area.removeEventListener('mouseover', enter)
    area.removeEventListener('mouseout', leave)
    element.removeEventListener('focus', focus)
    element.removeEventListener('blur', blur)
    element.removeEventListener('keydown', key)
    if (isLink) element.removeEventListener('click', follow)
    delete area.dataset.popoverArea
    popover.dispose()
  }
})
